<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>

  <header>
    <div class="headline row-start-center gap-[1rem]">
      <h1>{{ headerTitle() }}</h1>
      @if (hasEntry()) {
        <p-tag [severity]="statusSeverity()" [value]="statusText()" />
      }
    </div>
    <div class="buttons">
      <span class="separator"></span>
      <p-button [label]="'Back' | translateWF: 'Back'" variant="outlined" icon="pi pi-arrow-left" (click)="back()" />
    </div>
  </header>

  <div class="content">
    @if (entry(); as je) {
      <p class="je-details__caption">
        {{ je.description }}
        <span class="je-details__caption-sep">·</span>{{ je.postingDate | date: 'dd/MM/yyyy' }} <span class="je-details__caption-sep">·</span
        >{{ 'JournalEntry_Period' | translateWF: 'Period' }} {{ periodLabel() }}
      </p>

      @if (je.reference) {
        <!-- Names the document this posting came from. Not yet a link: the entry stores the source as a bare
             number, so there is nothing to navigate to without a lookup-by-number — #5028 item 2 turns this
             into a real cross-link once the source is persisted as an EntityKey. -->
        <div class="je-details__origin">
          <span class="pi pi-link"></span>
          <span>
            {{ 'JournalEntry_GeneratedFrom' | translateWF: 'Generated from' }} {{ sourceModuleText() }}
            <span class="font-medium">#{{ je.reference }}</span>
          </span>
        </div>
      }

      <div class="grid-12 gap-[1.5rem]">
        <!-- LEFT — the posting itself -->
        <div class="span-12-12-8 col">
          <p-panel [header]="'JournalEntry_Lines' | translateWF: 'Lines'">
            <p-card data-variant="compact">
              <p-table [value]="je.lineItems" dataKey="lineNumber" styleClass="p-datatable-sm">
                <ng-template pTemplate="header">
                  <tr>
                    <th class="je-details__line-col">#</th>
                    <th>{{ 'JournalEntry_Account' | translateWF: 'Account' }}</th>
                    <th>{{ 'JournalEntry_CostCenter' | translateWF: 'Cost center' }}</th>
                    <th class="text-right">{{ 'JournalEntry_Debit' | translateWF: 'Debit' }}</th>
                    <th class="text-right">{{ 'JournalEntry_Credit' | translateWF: 'Credit' }}</th>
                  </tr>
                </ng-template>

                <ng-template pTemplate="body" let-line>
                  <tr>
                    <td class="je-details__line-col">{{ line.lineNumber }}</td>
                    <td>
                      <div>{{ line.accountName }}</div>
                      <div class="je-details__muted">{{ line.accountNumber }}</div>
                      @if (line.description) {
                        <div class="je-details__muted">{{ line.description }}</div>
                      }
                    </td>
                    <td>{{ line.costCenter || '—' }}</td>
                    <!-- A line is either a debit or a credit; the other side stays blank rather than showing
                       0.00, so the two columns read as one ledger and not a grid half-full of zeroes.

                       The BOOKS' figure leads on every line, and what actually moved sits beneath it in
                       small text with the rate that got there — quoted the way a person says a rate, units
                       per dollar, rather than the reciprocal the GL multiplies by. A reader comparing two
                       lines of one entry is then comparing dollars to dollars, which is the only comparison
                       that means anything once the bank has converted. A functional-only line (the realized
                       exchange difference) has no transaction amount at all and renders its functional
                       figure directly. -->
                    <td class="text-right tabular-nums">
                      @if (isFunctionalOnly(line)) {
                        @if (line.functionalDebitAmount) {
                          {{ line.functionalDebitAmount | number: '1.2-2' }} <span class="je-details__muted">{{ functionalCurrencyText() }}</span>
                        }
                      } @else if (line.debitAmount) {
                        @if (showsConversion(line)) {
                          {{ line.functionalDebitAmount | number: '1.2-2' }} <span class="je-details__muted">{{ functionalCurrencyText() }}</span>
                          <div class="je-details__muted">{{ line.debitAmount | number: '1.2-2' }} {{ effectiveCurrencyText(line) }} &#64; {{ lineRateText(line) }}</div>
                        } @else {
                          {{ line.debitAmount | number: '1.2-2' }}
                        }
                      }
                    </td>
                    <td class="text-right tabular-nums">
                      @if (isFunctionalOnly(line)) {
                        @if (line.functionalCreditAmount) {
                          {{ line.functionalCreditAmount | number: '1.2-2' }} <span class="je-details__muted">{{ functionalCurrencyText() }}</span>
                        }
                      } @else if (line.creditAmount) {
                        @if (showsConversion(line)) {
                          {{ line.functionalCreditAmount | number: '1.2-2' }} <span class="je-details__muted">{{ functionalCurrencyText() }}</span>
                          <div class="je-details__muted">{{ line.creditAmount | number: '1.2-2' }} {{ effectiveCurrencyText(line) }} &#64; {{ lineRateText(line) }}</div>
                        } @else {
                          {{ line.creditAmount | number: '1.2-2' }}
                        }
                      }
                    </td>
                  </tr>
                </ng-template>

                <ng-template pTemplate="footer">
                  <tr class="je-details__totals">
                    <!-- Always totalled in the BOOKS' currency, whatever the entry was written in — that is the
                       one currency every line of every entry shares, so it is the only total that compares
                       with the entry above it. Where the entry is in a single foreign currency, its own
                       total follows as "books / transaction"; a mixed entry has no such figure to show, since
                       its transaction total would be a sum across currencies (#5144). -->
                    <td colspan="3">{{ 'JournalEntry_TotalFunctional' | translateWF: 'Total (books)' }}</td>
                    <td class="text-right tabular-nums">
                      {{ je.totalFunctionalDebits | number: '1.2-2' }} {{ functionalCurrencyText() }}
                      @if (showsTransactionTotals()) {
                        <span class="je-details__muted">/ {{ je.totalDebits | number: '1.2-2' }} {{ currencyText() }}</span>
                      }
                    </td>
                    <td class="text-right tabular-nums">
                      {{ je.totalFunctionalCredits | number: '1.2-2' }} {{ functionalCurrencyText() }}
                      @if (showsTransactionTotals()) {
                        <span class="je-details__muted">/ {{ je.totalCredits | number: '1.2-2' }} {{ currencyText() }}</span>
                      }
                    </td>
                  </tr>
                </ng-template>

                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td colspan="5">{{ 'General_NoRecords' | translateWF: 'No records found' }}</td>
                  </tr>
                </ng-template>
              </p-table>

              <div class="je-details__balance" [class.je-details__balance--off]="!isBalanced()">
                <span class="je-details__balance-state">
                  @if (isBalanced()) {
                    <span class="pi pi-check-circle"></span>
                    {{ 'JournalEntry_Balanced' | translateWF: 'Balanced' }}
                  } @else {
                    <span class="pi pi-exclamation-triangle"></span>
                    {{ 'JournalEntry_Unbalanced' | translateWF: 'Unbalanced' }}
                  }
                </span>
                <!-- What the entry is worth in the books, and at what rate. Only meaningful when the two
                   currencies differ; on a same-currency entry it would just repeat the total. -->
                @if (isMixedCurrency()) {
                  <span class="je-details__muted">
                    {{
                      'JournalEntry_MixedNote' | translateWF: "Lines are in more than one currency, each converting at its own rate — the entry balances in the books' currency."
                    }}
                  </span>
                } @else if (isForeignCurrency()) {
                  <span class="je-details__muted tabular-nums">
                    ≈ {{ je.totalFunctionalDebits | number: '1.2-2' }} {{ functionalCurrencyText() }} · 1 {{ currencyText() }} = {{ je.exchangeRate | number: '1.2-4' }}
                    {{ functionalCurrencyText() }}
                  </span>
                }
              </div>
            </p-card>
          </p-panel>
        </div>

        <!-- RIGHT — what produced the entry, and when -->
        <div class="span-12-12-4 col">
          <p-panel [header]="'JournalEntry_Details' | translateWF: 'Entry details'">
            <p-card>
              <div class="summary-rows">
                <div class="summary-rows__row">
                  <span>{{ 'JournalEntry_DocumentDate' | translateWF: 'Document date' }}</span>
                  <span class="ml-auto">{{ je.documentDate | date: 'dd/MM/yyyy' }}</span>
                </div>
                <div class="summary-rows__row">
                  <span>{{ 'JournalEntry_PostingDate' | translateWF: 'Posting date' }}</span>
                  <span class="ml-auto">{{ je.postingDate | date: 'dd/MM/yyyy' }}</span>
                </div>
                <div class="summary-rows__row">
                  <span>{{ 'JournalEntry_Period' | translateWF: 'Period' }}</span>
                  <span class="ml-auto tabular-nums">{{ periodLabel() }}</span>
                </div>
                <div class="summary-rows__row">
                  <span>{{ 'Currency' | translateWF: 'Currency' }}</span>
                  <span class="ml-auto">{{ currencyText() }}</span>
                </div>
                @if (isForeignCurrency()) {
                  <div class="summary-rows__row">
                    <span>{{ 'JournalEntry_ExchangeRate' | translateWF: 'Exchange rate' }}</span>
                    <span class="ml-auto tabular-nums">{{ je.exchangeRate | number: '1.2-4' }}</span>
                  </div>
                }
                <div class="summary-rows__row">
                  <span>{{ 'JournalEntry_Source' | translateWF: 'Source module' }}</span>
                  <p-tag class="ml-auto" severity="info" [rounded]="true" [value]="sourceModuleText()" />
                </div>
                @if (je.reference) {
                  <div class="summary-rows__row">
                    <span>{{ 'JournalEntry_Reference' | translateWF: 'Reference' }}</span>
                    <span class="ml-auto font-mono">{{ je.reference }}</span>
                  </div>
                }
              </div>
            </p-card>
          </p-panel>

          <p-panel [header]="'JournalEntry_AuditTrail' | translateWF: 'Audit trail'">
            <p-card>
              <div class="je-details__audit">
                @if (je.createdDate) {
                  <div class="je-details__audit-row">
                    <span class="pi pi-pencil"></span>
                    <div>
                      <div>{{ 'CreatedBy' | translateWF: 'Created by' }} {{ je.createdBy || ('System' | translateWF: 'System') }}</div>
                      <div class="je-details__muted">{{ je.createdDate | date: 'dd/MM/yyyy HH:mm' }}</div>
                    </div>
                  </div>
                }
                @if (je.modifiedDate) {
                  <div class="je-details__audit-row">
                    <span class="pi pi-check-circle"></span>
                    <div>
                      <div>{{ 'ModifiedBy' | translateWF: 'Last updated by' }} {{ je.modifiedBy || ('System' | translateWF: 'System') }}</div>
                      <div class="je-details__muted">{{ je.modifiedDate | date: 'dd/MM/yyyy HH:mm' }}</div>
                    </div>
                  </div>
                }
                <!-- Both sides of a reversal name each other by number. These resolve to the other entry, so a
                   reader can follow the pair without going back through the list. -->
                @if (je.reversesDocumentNumber) {
                  <div class="je-details__audit-row">
                    <span class="pi pi-replay"></span>
                    <div>
                      <div>{{ 'JournalEntry_Reverses' | translateWF: 'Reverses' }}</div>
                      <a class="cursor-pointer text-primary hover:underline font-mono" (click)="openByNumber(je.reversesDocumentNumber)">
                        {{ je.reversesDocumentNumber }}
                      </a>
                    </div>
                  </div>
                }
                @if (je.reversedByDocumentNumber) {
                  <div class="je-details__audit-row">
                    <span class="pi pi-undo"></span>
                    <div>
                      <div>{{ 'JournalEntry_ReversedBy' | translateWF: 'Reversed by' }}</div>
                      <a class="cursor-pointer text-primary hover:underline font-mono" (click)="openByNumber(je.reversedByDocumentNumber)">
                        {{ je.reversedByDocumentNumber }}
                      </a>
                    </div>
                  </div>
                }
              </div>
            </p-card>
          </p-panel>
        </div>
      </div>
    } @else if (isLoading() === 0) {
      <p-message severity="warn" [text]="'JournalEntry_NotFound' | translateWF: 'Journal entry not found.'" />
    }
  </div>
</main>
